import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  Plane,
  BedDouble,
  Palmtree,
  Bus,
  ChevronDown,
  Menu,
  Mail,
  Phone,
  UserRound,
  CalendarSearch,
  CircleUserRound,
  LogOut,
  ShoppingCart,
} from "lucide-react";
import { ASSETS } from "@/mock";
import { useTenant, BrandLogo } from "@/context/TenantContext";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import NotificationBell from "@/components/NotificationBell";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const NAV = [
  { label: "Flights", icon: Plane, to: "/" },
  { label: "Hotels", icon: BedDouble, to: "/hotel-page" },
  { label: "Holiday", icon: Palmtree, to: "/package" },
  { label: "Bus", icon: Bus, to: "/bus" },
];

const MORE_LINKS = [
  { label: "Visa", to: "/visa" },
  { label: "Group Booking", to: "/group-enquiry" },
  { label: "Cab Enquiry", to: "/cab-enquiry" },
  { label: "Train Enquiry", to: "/train-enquiry" },
  { label: "Web Check-in", to: "/web-checkin" },
  { label: "Contact Us", to: "/contact" },
  { label: "Flight Tracking", to: "https://flightradar24.com/", external: true },
];

export const TopBar = () => {
  const { contact: CONTACT, isWhiteLabel } = useTenant();
  return (
  <div className="hidden md:block border-b border-slate-100 bg-white">
    <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-1.5 text-[13px] text-slate-600">
      <a
        href={`mailto:${CONTACT.email}`}
        className="flex items-center gap-1.5 hover:text-[#1B1F5B]"
      >
        <Mail className="h-3.5 w-3.5 text-[#1B1F5B]" /> {CONTACT.email}
      </a>
      <div className="flex items-center gap-5">
        {!isWhiteLabel && (
          <Link to="/agency-signup" data-testid="topbar-agency-signup-link"
            className="rounded-full bg-[#F5B32B]/15 px-2.5 py-0.5 font-semibold text-[#B57A00] hover:bg-[#F5B32B]/25">
            Start Your Agency
          </Link>
        )}
        <Link
          to="/login?tab=agent"
          className="flex items-center gap-1.5 hover:text-[#1B1F5B]"
        >
          <UserRound className="h-3.5 w-3.5 text-[#1B1F5B]" /> Partner Login
        </Link>
        <Link
          to="/dashboard"
          className="flex items-center gap-1.5 hover:text-[#1B1F5B]"
        >
          <CalendarSearch className="h-3.5 w-3.5 text-[#1B1F5B]" /> Find Booking
        </Link>
        <a
          href={`tel:${CONTACT.phone}`}
          className="flex items-center gap-1.5 font-medium hover:text-[#1B1F5B]"
        >
          <Phone className="h-3.5 w-3.5 text-[#1B1F5B]" /> {CONTACT.phone}
        </a>
      </div>
    </div>
  </div>
);
};

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { count } = useCart();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isActive = (to) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  return (
    <header
      className={`sticky top-0 z-50 bg-white transition-shadow duration-300 ${
        scrolled ? "shadow-[0_2px_20px_rgba(27,31,91,0.10)]" : ""
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-2.5">
        <Link to="/" className="flex shrink-0 items-center">
          <BrandLogo className="h-11 w-auto md:h-12" testId="header-brand-logo" />
        </Link>

        {/* Desktop Nav — hidden for admin (admin manages, doesn't book) */}
        {user?.role !== "admin" && (
        <nav className="hidden items-center gap-1 lg:flex">
          {NAV.map((item) => (
            <Link
              key={item.label}
              to={item.to}
              className={`relative flex items-center gap-1.5 rounded-md px-4 py-2 text-[15px] font-medium transition-colors duration-200 ${
                isActive(item.to)
                  ? "text-[#1B1F5B]"
                  : "text-slate-600 hover:text-[#1B1F5B]"
              }`}
            >
              <item.icon className="h-4 w-4" />
              {item.label}
              {isActive(item.to) && (
                <span className="absolute inset-x-3 -bottom-[13px] h-[3px] rounded-full bg-[#1B1F5B]" />
              )}
            </Link>
          ))}
          <DropdownMenu>
            <DropdownMenuTrigger className="flex items-center gap-1.5 rounded-md px-4 py-2 text-[15px] font-medium text-slate-600 outline-none transition-colors hover:text-[#1B1F5B]">
              More <ChevronDown className="h-4 w-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              {MORE_LINKS.map((l) =>
                l.external ? (
                  <DropdownMenuItem key={l.label} asChild>
                    <a href={l.to} target="_blank" rel="noreferrer" className="cursor-pointer">
                      {l.label}
                    </a>
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem key={l.label} asChild>
                    <Link to={l.to} className="cursor-pointer">
                      {l.label}
                    </Link>
                  </DropdownMenuItem>
                )
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>
        )}

        <div className="flex items-center gap-3">
          {user?.role !== "admin" && (
            <Link to="/cart" data-testid="header-cart" className="relative flex h-10 w-10 items-center justify-center rounded-lg text-slate-600 transition-colors hover:bg-slate-100 hover:text-[#1B1F5B]">
              <ShoppingCart className="h-5 w-5" />
              {count > 0 && (
                <span data-testid="header-cart-badge" className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#F5B32B] px-1 text-[10px] font-bold text-[#1B1F5B]">{count}</span>
              )}
            </Link>
          )}
          {user && <NotificationBell />}
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-2 rounded-lg bg-[#1B1F5B] px-4 py-2.5 text-sm font-semibold text-white outline-none transition-colors hover:bg-[#141844]">
                <CircleUserRound className="h-4.5 w-4.5" />
                <span className="hidden sm:inline">{user.name.split(" ")[0]}</span>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem asChild>
                  <Link to="/dashboard" className="cursor-pointer">My Dashboard</Link>
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="cursor-pointer text-red-600"
                  onClick={() => {
                    logout();
                    navigate("/");
                  }}
                >
                  <LogOut className="mr-2 h-4 w-4" /> Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <button
              onClick={() => navigate("/login")}
              className="flex items-center gap-2 rounded-lg bg-[#1B1F5B] px-4 py-2.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#141844]"
            >
              <CircleUserRound className="h-4.5 w-4.5" />
              <span className="hidden sm:inline">Log in/Sign up</span>
              <span className="sm:hidden">Login</span>
            </button>
          )}

          {/* Currency selector */}
          <DropdownMenu>
            <DropdownMenuTrigger className="hidden items-center gap-1.5 outline-none sm:flex">
              <img src={ASSETS.indiaFlag} alt="IN" className="h-5 w-7 rounded-[3px] object-cover shadow-sm" />
              <span className="flex h-7 w-7 items-center justify-center rounded-md bg-[#1B1F5B] text-sm font-bold text-white">
                ₹
              </span>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <div className="px-2 py-1.5 text-xs font-semibold uppercase text-slate-400">
                All Currencies
              </div>
              <DropdownMenuItem className="cursor-pointer gap-2">
                <span className="font-bold text-[#1B1F5B]">₹</span> INR (India)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Mobile menu */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger className="lg:hidden">
              <Menu className="h-6 w-6 text-[#1B1F5B]" />
            </SheetTrigger>
            <SheetContent side="right" className="w-72">
              <div className="mt-6 flex flex-col gap-1">
                {user?.role !== "admin" && NAV.map((item) => (
                  <Link
                    key={item.label}
                    to={item.to}
                    onClick={() => setOpen(false)}
                    className={`flex items-center gap-3 rounded-lg px-3 py-3 text-[15px] font-medium ${
                      isActive(item.to)
                        ? "bg-[#1B1F5B]/5 text-[#1B1F5B]"
                        : "text-slate-600"
                    }`}
                  >
                    <item.icon className="h-5 w-5" /> {item.label}
                  </Link>
                ))}
                <div className="mt-2 border-t pt-2">
                  {user?.role !== "admin" && MORE_LINKS.map((l) =>
                    l.external ? (
                      <a
                        key={l.label}
                        href={l.to}
                        target="_blank"
                        rel="noreferrer"
                        className="block rounded-lg px-3 py-2.5 text-sm text-slate-600"
                      >
                        {l.label}
                      </a>
                    ) : (
                      <Link
                        key={l.label}
                        to={l.to}
                        onClick={() => setOpen(false)}
                        className="block rounded-lg px-3 py-2.5 text-sm text-slate-600"
                      >
                        {l.label}
                      </Link>
                    )
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
